import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useSnackbar } from 'notistack'

import { AppContext, useHost } from '../../providers/app-provider'
import AccountsContext from '../../routes/accounts/context'
import DeviceContext from '../../routes/devices/context'
import Api from '../api'

/**
 * This hook loads the device photo or thumbnail based on the provided deviceId and type. This hook is required for loading device photos as the endpoint requires the authentication token.
 * @param deviceId - The ID of the device whose photo is to be loaded.
 * @param shouldLoad - A boolean indicating whether to load the photo. This should be `true` if `device.hasPhoto` is `true`.
 * @param type - The type of photo to load, either 'thumb' or 'photo'.
 * @returns An object containing the photo path and a function to set the photo path, which is useful when overriding it with a local image url when uploading a new image.
 * @example
 * const { photoPath, setPhotoPath } = useLoadDevicePhoto(device.id, device.hasPhoto, 'photo')
 * // Use photoPath to display the device photo
 * // Use setPhotoPath to update the photo path when a new photo is uploaded
 */
const useLoadDevicePhoto = (deviceId: number | undefined, shouldLoad: boolean, type: 'thumb' | 'photo') => {
  const auth = useAuth()
  const host = useHost()
  const { enqueueSnackbar } = useSnackbar()

  const { devicePhotoRefreshCounter: deviceAccountsRefreshCounter } = useContext(AccountsContext)
  const { devicePhotoRefreshCounter } = useContext(DeviceContext)
  const { pageRefreshCounter } = useContext(AppContext)

  const [loadedPhotoPath, setLoadedPhotoPath] = useState<string | null>(null)
  const [localPhotoPath, setLocalPhotoPath] = useState<string | null>(null)
  const hasPhotoPath = !!loadedPhotoPath

  const setPhotoPath = (path: string | null) => setLocalPhotoPath(path)

  const loadPhoto = useEffectEvent(async () => {
    if (!deviceId || !auth.user?.access_token || !host) return
    try {
      const response = await Api.get(`${host}/api/v2/devices/${deviceId}/${type}`, {}, { isFile: true })
      if (response.ok && response instanceof Response) {
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result
          if (base64String) setLoadedPhotoPath(base64String as string)
        }
        reader.readAsDataURL(blob) // Read the file as a data URL
      }
    } catch {
      enqueueSnackbar('Error loading device photo', { variant: 'error' })
    }
  })
  // Reset the photo path when the deviceId changes to ensure that the previous photo is not displayed for a new device
  useEffect(() => {
    setLoadedPhotoPath(null)
    setLocalPhotoPath(null)
  }, [deviceId])

  useEffect(() => {
    if (shouldLoad && !hasPhotoPath) loadPhoto()
    if (shouldLoad && (deviceAccountsRefreshCounter > 0 || devicePhotoRefreshCounter > 0)) loadPhoto()
  }, [deviceId, type, shouldLoad, deviceAccountsRefreshCounter, hasPhotoPath, devicePhotoRefreshCounter, host, pageRefreshCounter])

  return { photoPath: localPhotoPath ?? loadedPhotoPath, setPhotoPath }
}

export default useLoadDevicePhoto
